import { initializeFrontendBlocks } from "@/utils/acf-blocks";
import { name } from "./block.json";
import { store, getElement, getContext } from "@wordpress/interactivity";
import { lock, unlock } from 'tua-body-scroll-lock'
import * as focusTrap from 'focus-trap';

interface Context {
	isMenuOpen: boolean;
	shouldLockWhenMenuIsOpen: boolean;
	isMenuClosed: boolean;
	focusSelector: string;
	isMenuTransitioning: boolean;
	focusTrap: any;
	megaMenuIntersectionObserver: any;
	megaMenuElementIsIntersecting: boolean;
	requestToggleMenu?: number;
	scrollIsLockedToDrawer: boolean;
}

/**
 * initializeBlock
 *
 * Adds custom JavaScript to the block HTML.
 *
 * @param object block The block html element.
 *
 * @return  void
 */
function initializeBlock( block: HTMLElement ) {
	const storeName = block.dataset.wpInteractive;

	if (!storeName) {
		return;
	}

	const innerblocksElement = block.querySelector<HTMLElement>(
		'.wp-block-site-mega-menu__innerblocks'
	);

	const megaMenuElement = block.querySelector<HTMLElement>(
		'.wp-block-site-mega-menu__mega-menu'
	);

	if (!innerblocksElement || !megaMenuElement) {
		return;
	}

	const { state, actions } = store( storeName, {
		state: {
			isMenuOpen: false,
			isMenuTransitioning: false,
			requestToggleMenu: 0,
			get isMenuClosed() {
				return !state.isMenuOpen && !state.isMenuTransitioning;
			}
		},
		actions: {
			onToggleMenuClick() {
				state.requestToggleMenu = Date.now();
			},
			handleMenuKeydown( event ) {
				if (event?.key !== 'Escape') {
					return;
				}

				const context = getContext<Context>();

				if (!context.isMenuOpen) {
					return;
				}

				actions.closeMenu();
			},
			handleClickOutsideDrawer( event ) {
				const context = getContext<Context>();

				if (!context.isMenuOpen) {
					return;
				}

				if (megaMenuElement?.contains( event.target )) {
					return;
				}

				if (event.target === megaMenuElement) {
					return;
				}

				if (event.target.dataset?.interactive === storeName) {
					return;
				}

				const interactiveParent = event.target.closest(`[data-wp-interactive="${storeName}"]`);

				if (interactiveParent) {
					return;
				}

				actions.closeMenu();
			},
			handleDrawerTransitionend(event) {
				if (event.propertyName !== 'transform') {
					return;
				}

				const context = getContext<Context>();

				if (event.target !== megaMenuElement) {
					return;
				}

				if (context.focusSelector) {
					const focusElement = megaMenuElement.querySelector<HTMLElement>(
						context.focusSelector
					);
					if (focusElement) {
						focusElement.focus();
					}
				}

				context.isMenuTransitioning = false;

				if (context.shouldLockWhenMenuIsOpen && context.isMenuOpen) {
					const scrollTop = innerblocksElement.scrollTop;

					if (!context.focusTrap) {
						context.focusTrap = focusTrap.createFocusTrap(megaMenuElement);
					}

					context.focusTrap.activate();

					// We tend to lose the scroll position when when activating the focus trap.
					// Here we try to prevent that by manually setting the scroll position for a few frames.
					// This way we can ensure that the scroll position is restored after the focus trap is activated.
					innerblocksElement.scrollTop = scrollTop;
					window.requestAnimationFrame(() => {
						innerblocksElement.scrollTop = scrollTop;
						window.requestAnimationFrame(() => {
							innerblocksElement.scrollTop = scrollTop;
							window.requestAnimationFrame(() => {
								innerblocksElement.scrollTop = scrollTop;
							});
						});
					});
				}
			},
			toggleMenu() {
				const context = getContext<Context>();

				context.isMenuOpen ? actions.closeMenu() : actions.openMenu();
			},
			openMenu() {
				const context = getContext<Context>();

				context.isMenuClosed = false;
				context.isMenuTransitioning = true;

				// The animation does not properly play if the element is hidden behind display: none on the overlay/wrapper.
				// For this reason we wait a couple of frames before opening the menu.

				let tries = 0;

				const maybeStartOpening = () => {
					tries++;

					const megaMenuElementStyles = window.getComputedStyle(megaMenuElement);

					if ('none' === megaMenuElementStyles.getPropertyValue("display")) {

						if (tries > 10) {
							// Something is very wrong if we get here, and there is likely a bug in the code.
							// Let's try to force the drawer to open.
							megaMenuElement.style.display = 'block';
							context.isMenuTransitioning = false;
							context.isMenuOpen = true;
							return;
						}

						window.requestAnimationFrame(maybeStartOpening);
						return;
					}

					context.isMenuOpen = true;
				}

				window.requestAnimationFrame(maybeStartOpening);
			},
			closeMenu() {
				const context = getContext<Context>();

				if (!context.isMenuOpen) {
					return;
				}

				const innerblocksElement = block.querySelector<HTMLElement>(
					'.wp-block-site-mega-menu__innerblocks'
				);

				if (!innerblocksElement) {
					return;
				}

				context.isMenuOpen = false;
				context.isMenuTransitioning = true;

				if (context.focusTrap) {
					context.focusTrap.deactivate();
				}

				if (context.scrollIsLockedToDrawer) {
					unlock(innerblocksElement);
					context.scrollIsLockedToDrawer = false;
				}
			},
		},
		callbacks: {
			init() {
				const context = getContext<Context>();

				context.megaMenuIntersectionObserver = new IntersectionObserver(([entry]) => {
					context.megaMenuElementIsIntersecting = entry.isIntersecting;
				});
				context.megaMenuIntersectionObserver.observe(megaMenuElement);
			},
			watch() {
				const context = getContext<Context>();
				context.isMenuClosed = !context.isMenuOpen && !context.isMenuTransitioning;

				// Mirror context to state
				state.isMenuOpen = context.isMenuOpen;
				state.isMenuTransitioning = context.isMenuTransitioning;

				if (state.requestToggleMenu) {
					state.requestToggleMenu = 0;
					actions.toggleMenu();
				}

				if (!innerblocksElement) {
					return;
				}

				if (context.shouldLockWhenMenuIsOpen && context.megaMenuElementIsIntersecting && context.isMenuOpen) {
					if (!context.scrollIsLockedToDrawer) {
						lock(innerblocksElement);
						context.scrollIsLockedToDrawer = true;
					}
				}
			},
		},
	});
}

initializeFrontendBlocks(name, initializeBlock);
